import * as React from 'react';
import { useState } from 'react';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import {
    FAVOURITE_DASHBOARDS_QUERY_KEY,
    useGetFavouriteDashboards,
} from 'routes/Home/hooks/favouriteDashboards';
import { LAST_VIEWED_DASHBOARDS_QUERY_KEY } from 'routes/Home/hooks/useGetLastViewedDashboards';
import { useTranslation } from 'react-i18next';
import Flex from 'ui/Flex/Flex';
import { useQueryClient } from '@tanstack/react-query';
import { DashboardT } from 'business/definitions';
import DashboardCard from 'ui/Card/DashboardCard';
import { Box } from '@mui/material';
import FavouriteDashboardManager from './FavouriteDashboardManager';
import { syncCacheOnPropertyChange } from '../helpers';
import Loading from 'components/Loading';
import FavouriteDashboardModal from './FavouriteDashboardModal';

const StyDisplayMoreFlex = styled(Flex, {
    name: 'StyDisplayMoreFlex',
})(() => ({
    marginTop: '40px',
    '& button': {
        textDecoration: 'underline',
    },
}));

type Props = {
    title: string;
    disableShowMore?: boolean;
};

const FavouriteDashboards = ({
    title,
    disableShowMore = false,
}: Props): JSX.Element => {
    const [showMore, setShowMore] = useState(false);
    const queryClient = useQueryClient();
    const [showFavouriteDashboardManager, setShowFavouriteDashboardManager] =
        useState(false);

    const { data: favouriteDashboards, isLoading } =
        useGetFavouriteDashboards();
    const { t } = useTranslation();

    const showDisplayMoreButton =
        !disableShowMore && favouriteDashboards
            ? favouriteDashboards.length > 5
            : false;

    const getFavouriteDashboardsToDisplay = (): DashboardT[] => {
        if (!favouriteDashboards) {
            return [];
        }

        if (disableShowMore || showMore) {
            return favouriteDashboards.filter((d) => d.isUserFavourite);
        }

        return favouriteDashboards.slice(0, 5).filter((d) => d.isUserFavourite);
    };

    const toggleShowMore = () => setShowMore((prevState) => !prevState);

    const onPropertyChange = (
        dashboard: DashboardT,
        property: keyof DashboardT,
        value: boolean,
    ) => {
        syncCacheOnPropertyChange(dashboard, property, value, queryClient, [
            FAVOURITE_DASHBOARDS_QUERY_KEY,
            LAST_VIEWED_DASHBOARDS_QUERY_KEY,
        ]);
    };

    const openFavouriteDashboardModal = () => {
        setShowFavouriteDashboardManager(true);
    };

    return (
        <>
            <Box sx={{ width: '100%' }} data-testid="favourite-dashboards">
                <Typography variant="h5" sx={{ mb: '1.5rem' }}>
                    {title}
                </Typography>
                {isLoading ? (
                    <Loading />
                ) : favouriteDashboards?.length === 0 ? (
                    <Flex
                        direction="column"
                        alignItems="center"
                        justifyContent="center"
                        gap={1}
                    >
                        <Typography>
                            {t('dashboards.noFavouriteDashboards')}
                        </Typography>
                        <Button onClick={openFavouriteDashboardModal}>
                            {t('dashboards.noFavouriteDashboardsButton')}
                        </Button>
                    </Flex>
                ) : (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, 250px)',
                            justifyContent: 'space-evenly',
                            gap: 2,
                        }}
                    >
                        {getFavouriteDashboardsToDisplay().map(
                            (favouriteDashboard) => (
                                <Box
                                    key={favouriteDashboard.slug}
                                    data-testid={`dashboard-item ${favouriteDashboard.title}`}
                                >
                                    <DashboardCard
                                        dashboard={favouriteDashboard}
                                        onPropertyChange={onPropertyChange}
                                    />
                                </Box>
                            ),
                        )}
                        <Box>
                            <FavouriteDashboardManager
                                openFavouriteDashboardModal={
                                    openFavouriteDashboardModal
                                }
                            />
                        </Box>
                    </Box>
                )}

                {showDisplayMoreButton && (
                    <StyDisplayMoreFlex justify="center">
                        <Button onClick={toggleShowMore}>
                            {showMore
                                ? t('home.favourites.seeLess')
                                : t('home.favourites.seeMore')}
                        </Button>
                    </StyDisplayMoreFlex>
                )}
            </Box>
            {showFavouriteDashboardManager && (
                <FavouriteDashboardModal
                    open={showFavouriteDashboardManager}
                    onClose={() => setShowFavouriteDashboardManager(false)}
                />
            )}
        </>
    );
};

export default FavouriteDashboards;
